import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * NavbarMobile Component
 * 
 * Mobile version of the landing page navbar with hamburger menu drawer.
 * Features:
 * - Hamburger icon (top-left)
 * - Logo (center)
 * - Slide-in drawer from left
 * - Login/Sign Up buttons in drawer
 */
const NavbarMobile = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { currentUser, userData } = useAuth();

  // Lock body scroll when drawer is open
  useBodyScrollLock(isDrawerOpen);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <nav className="navbar-mobile flex justify-between items-center px-3 md:px-4 py-3 md:py-4 w-full relative z-navbar glass-bg-navbar shadow-navbar min-h-[60px]">
        {/* Hamburger Menu Button */}
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="hamburger-btn p-2 rounded-lg cursor-pointer transition-all duration-300 active:scale-[0.98]"
          aria-label="Open menu"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Menu size={24} className="text-text-primary" />
        </button>

        {/* Logo - Center */}
        <Link
          to="/"
          className="logo font-semibold text-logo flex items-center gap-2 text-text-primary whitespace-nowrap no-underline"
        >
          <span className="logo-icon font-bold lawnance-brand">LAWNANCE</span>
        </Link>

        {/* Spacer for symmetry */}
        <div className="w-10" />
      </nav>

      {/* Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.2,
                ease: 'linear'
              }}
              onClick={handleDrawerClose}
              className="fixed inset-0 bg-black/60 z-[200]"
              data-framer-motion="true"
              style={{
                willChange: 'opacity',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                pointerEvents: 'auto',
              }}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{
                duration: 0.25,
                ease: 'easeOut'
              }}
              className="fixed top-0 left-0 h-screen w-[85vw] max-w-[320px] z-[201]"
              data-framer-motion="true"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                willChange: 'transform',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                isolation: 'isolate',
                contain: 'layout style paint',
                pointerEvents: 'auto',
              }}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <span className="text-logo font-bold text-text-primary lawnance-brand">LAWNANCE</span>
                <button
                  onClick={handleDrawerClose}
                  className="p-2 rounded-lg cursor-pointer transition-all duration-300 active:scale-[0.98]"
                  aria-label="Close menu"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <X size={20} className="text-text-primary" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex flex-col p-4 md:p-6 gap-4">
                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-white/10">
                  {currentUser ? (
                    <div className="flex flex-col gap-3">
                      <span className="text-text-primary font-medium text-center">
                        Welcome, {userData?.fullName || currentUser?.displayName || 'User'}
                      </span>
                      <Link
                        to="/app"
                        onClick={handleDrawerClose}
                        className="btn-primary bg-btn-bg text-btn-text border border-white/20 px-4 md:px-6 py-2.5 md:py-3 rounded-btn-primary text-nav-btn font-medium cursor-pointer transition-all duration-300 no-underline shadow-btn-primary hover:shadow-btn-primary-hover hover:-translate-y-0.5 active:scale-[0.98] text-center"
                        style={{
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                        }}
                      >
                        Go to App
                      </Link>
                    </div>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={handleDrawerClose}
                        className="btn-outline bg-transparent border border-white/20 px-4 md:px-6 py-2.5 md:py-3 rounded-btn-outline text-nav-btn font-medium cursor-pointer transition-all duration-300 no-underline text-text-primary hover:bg-white/10 hover:border-white/30 hover:-translate-y-0.5 hover:shadow-btn-outline-hover active:scale-[0.98] text-center"
                        style={{
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                        }}
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        onClick={handleDrawerClose}
                        className="btn-primary bg-btn-bg text-btn-text border border-white/20 px-4 md:px-6 py-2.5 md:py-3 rounded-btn-primary text-nav-btn font-medium cursor-pointer transition-all duration-300 no-underline shadow-btn-primary hover:shadow-btn-primary-hover hover:-translate-y-0.5 active:scale-[0.98] text-center"
                        style={{
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                        }}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavbarMobile;

